import { useState } from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';

import './login.css';

import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { checkPasswordRequirement, validatePassword } from '../../../utils/password.util';

import Alert from '@mui/material/Alert';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BsArrowRepeat } from 'react-icons/bs';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userHasAuthenticated } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isPasswordChangeRequired, setIsPasswordChangeRequired] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get redirect path from query parameter
  const redirectPath = searchParams.get('redirect') || '/service-forms';

  function validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function validateForm() {
    return email.length > 0 && password.length > 0 && validateEmail(email);
  }

  function getErrorMessage(error: any): string {
    const errorMessage = error?.message || error?.toString() || '';
    const errorCode = error?.name || error?.code || '';

    // Handle specific Cognito error codes
    if (errorCode === 'NotAuthorizedException' || errorMessage.includes('Incorrect username or password')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (errorCode === 'UserNotConfirmedException' || errorMessage.includes('User is not confirmed')) {
      return 'Please verify your email address before logging in.';
    }
    if (errorCode === 'UserNotFoundException' || errorMessage.includes('User does not exist')) {
      return 'Account not found. Please check your email address.';
    }
    if (errorCode === 'TooManyRequestsException' || errorMessage.includes('Too many requests')) {
      return 'Too many login attempts. Please try again later.';
    }
    if (errorCode === 'PasswordResetRequiredException' || errorMessage.includes('Password reset required')) {
      return 'Password reset required. Please contact support.';
    }
    if (errorMessage.includes('User is disabled') || errorMessage.includes('User account is disabled')) {
      return 'Your account has been disabled. Please contact support.';
    }
    if (errorMessage.includes('User account is locked') || errorMessage.includes('User is locked')) {
      return 'Your account is temporarily locked. Please try again later.';
    }
    if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    }

    // Return the error message if it's user-friendly, otherwise return a generic message
    if (errorMessage && !errorMessage.includes('AWS') && !errorMessage.includes('Cognito')) {
      return errorMessage;
    }

    return 'An error occurred during sign in. Please try again.';
  }

  async function handlePasswordChange(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      setIsLoading(false);
      return;
    }

    try {
      await confirmSignIn({ challengeResponse: newPassword });
      userHasAuthenticated(true);
      navigate(redirectPath);
    } catch (e: any) {
      onError(e);
      setErrorMessage(getErrorMessage(e));
      setIsLoading(false);
    }
  }

  async function handleSubmit(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');

    // Validate email format
    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      const signInResult = await signIn({ username: email, password });

      // Check if password change is required
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setIsPasswordChangeRequired(true);
        setIsLoading(false);
      } else {
        userHasAuthenticated(true);
        navigate(redirectPath);
      }
    } catch (e: any) {
      onError(e);
      setErrorMessage(getErrorMessage(e));
      setIsLoading(false);
    }
  }

  return (
    <div id="login-section">
      <div id="bg"></div>
      <section id="login">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 mx-auto my-auto">
              <img alt="TSF" id="tsf-logo" src="/assets/images/trailer-solutions-fl-logo.png" />
            </div>
            <div className="col-12">
              <form onSubmit={isPasswordChangeRequired ? handlePasswordChange : handleSubmit}>
                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                  </Alert>
                )}
                {isPasswordChangeRequired && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please set your new password to continue.
                  </Alert>
                )}
                {!isPasswordChangeRequired ? (
                  <>
                    <label className="d-none">Email</label>
                    <input
                      className="form-control mb-2"
                      required
                      id="email"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="Email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                    />
                    <label className="d-none">Password</label>
                    <div className="position-relative">
                      <input
                        className="form-control"
                        required
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (errorMessage) setErrorMessage('');
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        style={{
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '0',
                          border: 'none',
                          background: 'none',
                          textDecoration: 'none',
                        }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    <div className="text-end mt-2 mb-3">
                      <a
                        href="/reset-password"
                        style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.875rem' }}
                      >
                        Forgot Password?
                      </a>
                    </div>
                    <button id="submit-btn" type="submit" disabled={!validateForm() || isLoading}>
                      {isLoading && <BsArrowRepeat className="spinning" />}
                      Login
                    </button>
                  </>
                ) : (
                  <>
                    <label className="d-none">New Password</label>
                    <div className="position-relative mb-2">
                      <input
                        className="form-control"
                        required
                        id="newPassword"
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        autoFocus
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (errorMessage) setErrorMessage('');
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        style={{
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '0',
                          border: 'none',
                          background: 'none',
                          textDecoration: 'none',
                        }}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {newPassword && (
                      <div className="mb-3" style={{ fontSize: '0.75rem', color: '#666' }}>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'length') ? '✓' : '✗'} At least 8 characters
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'uppercase') ? '✓' : '✗'} At least one uppercase letter
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'lowercase') ? '✓' : '✗'} At least one lowercase letter
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'number') ? '✓' : '✗'} At least one number
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'special') ? '✓' : '✗'} At least one special character
                        </div>
                      </div>
                    )}
                    <label className="d-none">Confirm Password</label>
                    <div className="position-relative mb-2">
                      <input
                        className="form-control"
                        required
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errorMessage) setErrorMessage('');
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        style={{
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '0',
                          border: 'none',
                          background: 'none',
                          textDecoration: 'none',
                        }}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {confirmPassword && (
                      <div className="mb-3" style={{ fontSize: '0.75rem' }}>
                        {newPassword === confirmPassword ? (
                          <span style={{ color: 'green' }}>✓ Passwords match</span>
                        ) : (
                          <span style={{ color: 'red' }}>✗ Passwords do not match</span>
                        )}
                      </div>
                    )}
                    <button id="submit-btn" type="submit" disabled={!newPassword || !confirmPassword || isLoading}>
                      {isLoading && <BsArrowRepeat className="spinning" />}
                      Set Password
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
